import React, { useState, useEffect, useMemo } from "react";
import { ApiKeyOrGetter } from "../../../../services/GeneralService";
import { IntentService } from "../../../../services/IntentService";
import { ApplicationService } from "../../../../services/ApplicationService";
import { DatasetService } from "../../../../services/DatasetService";
import { DatasetData } from "../../../../model_gen/dataset";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { Badge } from "../../../ui/badge";
import {
  CheckCircleIcon,
  DatabaseIcon,
  CloudUploadIcon,
  DollarSignIcon,
  FileTextIcon,
  InfoIcon,
  TagIcon,
  SparklesIcon,
} from "lucide-react";
import { cn } from "../../../../lib/utils";
import { useTagConfig } from "../../../../hooks/useTagConfig";
import { GENERATION_DOMAINS } from "./DatasetGenerateConfig";
import { mergeDatasetGenerateMeta } from "./generate-config/datasetGenerateMeta";
import {
  normalizeGenerationVariableFromMeta,
  estimateCost,
  formatTokenCount,
  getGenerationCostDimensionsFromMeta,
} from "./generate-config/types";

interface DatasetReviewProps {
  datasetData: DatasetData;
  apiKey: ApiKeyOrGetter;
}

interface SummarySectionProps {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const SummarySection: React.FC<SummarySectionProps> = ({ title, icon, children }) => (
  <section className="space-y-3">
    <div className="flex items-center gap-2 border-b border-border pb-2">
      {icon ? <span className="text-maitai-green shrink-0">{icon}</span> : null}
      <h3 className="text-sm font-gc-demi-bold text-foreground tracking-tight">{title}</h3>
    </div>
    <div className="space-y-3">{children}</div>
  </section>
);

interface SummaryRowProps {
  label: string;
  children: React.ReactNode;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ label, children }) => (
  <div className="grid grid-cols-1 gap-1 sm:grid-cols-[10.5rem_minmax(0,1fr)] sm:gap-4 text-sm items-start">
    <div className="text-muted-foreground font-gc-med shrink-0">{label}</div>
    <div className="text-foreground font-gc-reg min-w-0">{children}</div>
  </div>
);

const formatModelLabel = (ref: string) =>
  ref.includes(":") ? ref.split(":").slice(1).join(":") : ref;

const teacherTypeLabel = (t: string | undefined) => {
  if (t === "workflow") return "Workflow";
  if (t === "agent") return "Agent";
  return "Model";
};

const generationModeLabel = (mode: string | undefined) =>
  mode === "structured" ? "Structured" : "Freeform";

interface VariableReviewRow {
  name: string;
  kind: "Seed" | "Computed";
}

const buildVariableReviewRows = (
  rawVars: unknown[],
  computedPh: { placeholder?: string }[]
): VariableReviewRow[] => {
  const rows: VariableReviewRow[] = [];
  const seen = new Set<string>();
  for (const raw of rawVars) {
    if (!raw || typeof raw !== "object") continue;
    const v = normalizeGenerationVariableFromMeta(raw as Parameters<typeof normalizeGenerationVariableFromMeta>[0]);
    const name = String(v.name || "").trim();
    if (!name) continue;
    seen.add(name);
    rows.push({
      name,
      kind: v.source === "computed" ? "Computed" : "Seed",
    });
  }
  for (const c of computedPh) {
    const name = String(c.placeholder || "").trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    rows.push({ name, kind: "Computed" });
  }
  return rows;
};

interface AiReviewSectionProps {
  title: string;
  children: React.ReactNode;
}

const AiReviewSection: React.FC<AiReviewSectionProps> = ({ title, children }) => (
  <section className="space-y-3">
    <h3 className="text-sm font-gc-demi-bold text-foreground border-b border-border pb-2">
      {title}
    </h3>
    <div className="space-y-4">{children}</div>
  </section>
);

const DatasetReview: React.FC<DatasetReviewProps> = ({
  datasetData,
  apiKey,
}) => {
  const { getTagDisplayConfig } = useTagConfig(apiKey);
  const [loading, setLoading] = useState<boolean>(false);
  const [baseTypeLabel, setBaseTypeLabel] = useState<string>("");
  const [intentGroupName, setIntentGroupName] = useState<string>("");
  const [applicationName, setApplicationName] = useState<string>("");
  const [intentName, setIntentName] = useState<string>("");

  const meta: any = mergeDatasetGenerateMeta(
    (datasetData.meta || {}) as Record<string, unknown>
  );
  const datasourceType = meta.datasource_type || meta.data_source_type || "production";

  const isManualUpload = datasourceType === "manual";
  const isS3Import = datasourceType === "s3";
  const isGenerate = datasourceType === "generate";
  const manualDatasetSize = datasetData.meta?.manual_dataset_size as number;
  const uploadedFile = datasetData.meta?.uploaded_file as File;
  const s3Url = meta.s3_url as string | undefined;

  const genModelRef = String(meta.generation_model || "").trim();
  const genSize =
    typeof meta.generation_size === "number"
      ? meta.generation_size
      : Number(meta.generation_size) || 0;
  const genIncludeReasoning = Boolean(meta.generation_include_reasoning);

  const [serverCostEstimate, setServerCostEstimate] = useState<{
    input_tokens: number;
    output_tokens: number;
    cost_usd: number;
    is_estimated_pricing: boolean;
    line_items?: Array<{
      key: string;
      label: string;
      calls: number;
      input_tokens: number;
      output_tokens: number;
      usd: number;
      note?: string;
    }>;
    assumptions?: string[];
  } | null>(null);

  const estimatePayload = useMemo(() => {
    if (!isGenerate || !genModelRef || genSize <= 0) return null;
    const dims = getGenerationCostDimensionsFromMeta(
      (datasetData.meta || {}) as Record<string, unknown>
    );
    return {
      model: genModelRef,
      size: genSize,
      include_reasoning: genIncludeReasoning,
      mode: String(meta.generation_mode || "open_ended"),
      teacher_type: String(meta.generation_teacher_type || "model"),
      seed_model: String(meta.generation_seed_model || ""),
      use_prod_seeds: Boolean(meta.generation_use_prod_seeds),
      ...dims,
    };
  }, [
    isGenerate,
    genModelRef,
    genSize,
    genIncludeReasoning,
    meta.generation_mode,
    meta.generation_teacher_type,
    meta.generation_seed_model,
    meta.generation_use_prod_seeds,
    meta.generation_variables,
    meta.generation_computed_placeholders,
    meta.generation_request_system_prompt,
    meta.generation_user_template,
    datasetData.meta,
  ]);

  const debouncedEstimateKey = useDebouncedValue(
    estimatePayload ? JSON.stringify(estimatePayload) : "",
    500
  );

  const clientCostEstimate = useMemo(
    () =>
      isGenerate && genModelRef && genSize > 0
        ? estimateCost(genModelRef, genSize, genIncludeReasoning)
        : null,
    [isGenerate, genModelRef, genSize, genIncludeReasoning]
  );

  useEffect(() => {
    if (!debouncedEstimateKey) {
      setServerCostEstimate(null);
      return;
    }
    let parsed: NonNullable<typeof estimatePayload>;
    try {
      parsed = JSON.parse(debouncedEstimateKey) as NonNullable<typeof estimatePayload>;
    } catch {
      setServerCostEstimate(null);
      return;
    }
    if (!parsed.model || parsed.size <= 0) {
      setServerCostEstimate(null);
      return;
    }
    const sub = DatasetService.estimateGenerationCost(
      {
        model: parsed.model,
        size: parsed.size,
        include_reasoning: parsed.include_reasoning,
        mode: parsed.mode,
        teacher_type: parsed.teacher_type,
        seed_model: parsed.seed_model,
        use_prod_seeds: parsed.use_prod_seeds,
        num_seed_variables: parsed.num_seed_variables,
        num_computed_variables: parsed.num_computed_variables,
        sequential_placeholders: parsed.sequential_placeholders,
      },
      null,
      apiKey
    ).subscribe(
      (r: any) =>
        setServerCostEstimate({
          input_tokens: r.input_tokens,
          output_tokens: r.output_tokens,
          cost_usd: r.cost_usd,
          is_estimated_pricing: r.is_estimated_pricing,
          line_items: r.line_items,
          assumptions: r.assumptions,
        }),
      () => setServerCostEstimate(null)
    );
    return () => sub.unsubscribe();
  }, [debouncedEstimateKey, apiKey]);

  const reviewCostEstimate = serverCostEstimate
    ? {
        inputTokens: serverCostEstimate.input_tokens,
        outputTokens: serverCostEstimate.output_tokens,
        costUsd: serverCostEstimate.cost_usd,
        isEstimatedPricing: serverCostEstimate.is_estimated_pricing,
        lineItems: serverCostEstimate.line_items ?? null,
        assumptions: serverCostEstimate.assumptions ?? null,
      }
    : clientCostEstimate
    ? {
        inputTokens: clientCostEstimate.inputTokens,
        outputTokens: clientCostEstimate.outputTokens,
        costUsd: clientCostEstimate.costUsd,
        isEstimatedPricing: clientCostEstimate.isEstimatedPricing,
        lineItems: null,
        assumptions: [
          "Could not load full estimate — showing rough teacher-only total from local pricing.",
        ],
      }
    : null;

  useEffect(() => {
    if (
      datasetData.base_type === "intent_group" &&
      datasetData.intent_group_id
    ) {
      setLoading(true);
      IntentService.getIntentGroup(
        datasetData.intent_group_id,
        false,
        setLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          const name =
            response.intent_name ||
            response.name ||
            `Intent Group ${datasetData.intent_group_id}`;
          setBaseTypeLabel("Intent Group");
          setIntentGroupName(name);
          setLoading(false);
        },
        (error: any) => {
          console.error("Error fetching intent group:", error);
          setBaseTypeLabel("Intent Group");
          setIntentGroupName(`Intent Group ${datasetData.intent_group_id}`);
          setLoading(false);
        }
      );
    } else if (
      datasetData.base_type === "application_intent" &&
      datasetData.application_id &&
      datasetData.intent_id
    ) {
      setLoading(true);
      ApplicationService.getApplication(
        datasetData.application_id,
        setLoading,
        apiKey
      ).subscribe(
        (appResponse: any) => {
          const appName =
            appResponse?.application_name ||
            appResponse?.name ||
            `Application ${datasetData.application_id}`;
          setApplicationName(appName);

          ApplicationService.getApplicationAction(
            datasetData.application_id,
            datasetData.intent_id,
            setLoading,
            apiKey
          ).subscribe(
            (intentResponse: any) => {
              const intName =
                intentResponse?.action_type ||
                `Intent ${datasetData.intent_id}`;
              setBaseTypeLabel("Application/Intent");
              setIntentName(intName);
              setLoading(false);
            },
            (error: any) => {
              console.error("Error fetching intent:", error);
              setBaseTypeLabel("Application/Intent");
              setIntentName(`Intent ${datasetData.intent_id}`);
              setLoading(false);
            }
          );
        },
        (error: any) => {
          console.error("Error fetching application:", error);
          setBaseTypeLabel("Application/Intent");
          setApplicationName(`Application ${datasetData.application_id}`);
          setIntentName(`Intent ${datasetData.intent_id}`);
          setLoading(false);
        }
      );
    }
  }, [datasetData, apiKey]);

  const getSampleTypeLabel = () => {
    if (isManualUpload) return "Manual file upload";
    if (isS3Import) return "Import from S3";
    if (isGenerate) return "AI-generated synthetic data";

    switch (datasetData.sample_type) {
      case "all":
        return "All available requests";
      case "random":
        return `Random sample (${datasetData.sample_percentage}%)`;
      case "none":
        return "None (Empty dataset)";
      default:
        return "Not specified";
    }
  };

  const getDatasetSize = () => {
    if (isManualUpload) {
      return `${manualDatasetSize || 0} requests`;
    }
    if (isS3Import) {
      return "Import will run after dataset creation";
    }
    if (isGenerate) {
      return `~${(meta.generation_size || 0).toLocaleString()} examples (generation will run after creation)`;
    }

    if (!datasetData.total_requests) return "0 requests";

    if (datasetData.sample_type === "all") {
      return `${datasetData.total_requests} requests`;
    } else if (
      datasetData.sample_type === "random" &&
      datasetData.sample_percentage
    ) {
      const size = Math.round(
        (datasetData.total_requests * datasetData.sample_percentage) / 100
      );
      return `${size} requests (${datasetData.sample_percentage}% of ${datasetData.total_requests})`;
    } else {
      return "0 requests";
    }
  };

  const getBaseTypeValue = () => {
    if (loading) return <Skeleton className="h-4 w-32" />;

    if (datasetData.base_type === "intent_group") {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-maitai-green/10 text-maitai-green">
              {baseTypeLabel}
            </span>
          </div>
          <div className="text-sm text-foreground font-gc-reg">{intentGroupName}</div>
        </div>
      );
    } else {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-maitai-green/10 text-maitai-green">
              {baseTypeLabel}
            </span>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-foreground font-gc-reg">Application: {applicationName}</div>
            <div className="text-sm text-foreground font-gc-reg">Intent: {intentName}</div>
          </div>
        </div>
      );
    }
  };

  const getDataSourceValue = () => {
    const label =
      datasourceType === "manual"
        ? "Manual Upload"
        : datasourceType === "s3"
          ? "S3 Import"
          : datasourceType === "generate"
            ? "Generate with AI"
            : "Production Data";

    const badgeClass =
      datasourceType === "generate"
        ? "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
        : datasourceType === "manual" || datasourceType === "s3"
          ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
          : "bg-maitai-green/10 text-maitai-green";

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
              badgeClass
            )}
          >
            {label}
          </span>
        </div>
        {isManualUpload && uploadedFile && (
          <div className="text-sm text-muted-foreground">
            {uploadedFile.name} ({(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB)
          </div>
        )}
        {isS3Import && s3Url && (
          <div className="text-sm text-muted-foreground font-mono truncate">
            {s3Url}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Review Dataset
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Review your dataset configuration before creating
        </p>
      </div>

      {/* Summary Card */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <FileTextIcon className="w-5 h-5 text-maitai-green" />
            Dataset Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <SummarySection title="Overview">
            <SummaryRow label="Name">
              <span className="font-gc-med text-foreground">
                {datasetData.name || "Unnamed Dataset"}
              </span>
            </SummaryRow>
            <SummaryRow label="Description">
              {datasetData.description ? (
                <span className="whitespace-pre-wrap">{datasetData.description}</span>
              ) : (
                <span className="text-muted-foreground italic">No description</span>
              )}
            </SummaryRow>
          </SummarySection>

          <SummarySection title="Scope" icon={<DatabaseIcon className="w-4 h-4" />}>
            <div>{getBaseTypeValue()}</div>
          </SummarySection>

          <SummarySection title="Source" icon={<CloudUploadIcon className="w-4 h-4" />}>
            {getDataSourceValue()}
            {!isManualUpload && !isS3Import && !isGenerate && (
              <SummaryRow label="Sampling">
                {getSampleTypeLabel()}
              </SummaryRow>
            )}
            {!isGenerate && (
              <SummaryRow label={isManualUpload ? "Dataset size" : "Estimated size"}>
                <span className="text-maitai-green font-gc-med">{getDatasetSize()}</span>
              </SummaryRow>
            )}
          </SummarySection>

          {isGenerate && (() => {
            const isStructured = meta.generation_mode === "structured";
            const useProdSeeds = Boolean(meta.generation_use_prod_seeds);
            const prodCount = (meta.generation_prod_seed_ids as string[] | undefined)?.length ?? 0;
            const genVarsRaw = Array.isArray(meta.generation_variables) ? meta.generation_variables : [];
            const computedPh = Array.isArray(meta.generation_computed_placeholders)
              ? (meta.generation_computed_placeholders as { placeholder?: string }[])
              : [];
            const variableRows = buildVariableReviewRows(genVarsRaw, computedPh);
            const wfInput = String(meta.generation_workflow_input || "").trim();
            const showWorkflowInput =
              meta.generation_teacher_type === "workflow" && wfInput.length > 0;
            const requestSystem =
              (meta.generation_request_system_prompt ||
                meta.generation_system_prompt ||
                "") as string;
            const schemaFields = Array.isArray(meta.generation_response_schema)
              ? (meta.generation_response_schema as { name?: string; type?: string; description?: string }[])
              : [];
            const responseValuePath = String(meta.generation_teacher_response_value_path || "").trim();
            const hasResponseSchemaSection =
              schemaFields.length > 0 || responseValuePath.length > 0;

            return (
              <div className="rounded-lg border border-border bg-muted/20 p-4 sm:p-5 space-y-8">
                <div className="flex items-center gap-2 text-sm font-gc-demi-bold text-foreground">
                  <SparklesIcon className="w-4 h-4 text-maitai-green" />
                  AI generation
                </div>

                <AiReviewSection title="Setup">
                  <SummaryRow label="Generation mode">
                    <Badge variant="outline" className="text-xs w-fit">
                      {generationModeLabel(meta.generation_mode)}
                    </Badge>
                  </SummaryRow>
                  <SummaryRow label="Dataset size">
                    ~{(meta.generation_size || 0).toLocaleString()} examples
                    {meta.generation_size_tier ? (
                      <span className="text-muted-foreground ml-2">
                        ({String(meta.generation_size_tier)} tier)
                      </span>
                    ) : null}
                  </SummaryRow>
                  <SummaryRow label="Dataset seeds">
                    {useProdSeeds ? (
                      <span>
                        {prodCount} production request{prodCount === 1 ? "" : "s"} selected
                      </span>
                    ) : meta.generation_seed_model ? (
                      <span className="font-mono text-xs sm:text-sm break-all">
                        {formatModelLabel(String(meta.generation_seed_model))}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        Synthetic seeds (default seed model)
                      </span>
                    )}
                  </SummaryRow>
                  {meta.generation_seed_instructions ? (
                    <div className="space-y-2">
                      <div className="text-xs font-gc-med text-muted-foreground">
                        {useProdSeeds ? "Evolution instructions" : "Seed instructions"}
                      </div>
                      <div className="text-sm text-foreground font-gc-reg bg-background border border-border p-3 rounded-lg whitespace-pre-wrap">
                        {meta.generation_seed_instructions as string}
                      </div>
                    </div>
                  ) : null}
                  {meta.generation_difficulty_cues ? (
                    <div className="space-y-2">
                      <div className="text-xs font-gc-med text-muted-foreground">
                        Difficulty progression
                      </div>
                      <div className="text-sm text-foreground font-gc-reg bg-background border border-border p-3 rounded-lg whitespace-pre-wrap">
                        {meta.generation_difficulty_cues as string}
                      </div>
                    </div>
                  ) : null}
                </AiReviewSection>

                <AiReviewSection title="Prompts">
                  {!isStructured ? (
                    <>
                      <SummaryRow label="Prompt domains">
                        <div className="flex flex-wrap gap-2">
                          {(meta.generation_domains as string[] || []).length > 0 ? (
                            (meta.generation_domains as string[]).map((domainId: string) => {
                              const domain = GENERATION_DOMAINS.find((d) => d.id === domainId);
                              return (
                                <Badge key={domainId} variant="outline" className="text-xs">
                                  {domain?.label || domainId}
                                </Badge>
                              );
                            })
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </div>
                      </SummaryRow>
                      {meta.generation_context ? (
                        <SummaryRow label="Domain context">
                          <span className="whitespace-pre-wrap text-sm">
                            {meta.generation_context as string}
                          </span>
                        </SummaryRow>
                      ) : null}
                    </>
                  ) : null}
                  {requestSystem.trim() ? (
                    <div className="space-y-2">
                      <div className="text-xs font-gc-med text-muted-foreground">
                        System prompt (dataset)
                      </div>
                      <div className="text-sm text-foreground font-gc-reg font-mono bg-background border border-border p-3 rounded-lg whitespace-pre-wrap">
                        {requestSystem}
                      </div>
                    </div>
                  ) : (
                    <SummaryRow label="System prompt (dataset)">
                      <span className="text-muted-foreground">—</span>
                    </SummaryRow>
                  )}
                  {isStructured ? (
                    <>
                      <div className="space-y-2">
                        <div className="text-xs font-gc-med text-muted-foreground">Variables</div>
                        {variableRows.length > 0 ? (
                          <div className="rounded-md border border-border overflow-hidden bg-background">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-muted/50">
                                  <th className="text-left px-3 py-2 font-gc-med text-muted-foreground">Name</th>
                                  <th className="text-left px-3 py-2 font-gc-med text-muted-foreground">Type</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-border">
                                {variableRows.map((row) => (
                                  <tr key={row.name}>
                                    <td className="px-3 py-2 font-mono text-foreground">{row.name}</td>
                                    <td className="px-3 py-2">
                                      <Badge
                                        variant="outline"
                                        className={cn(
                                          "text-[10px]",
                                          row.kind === "Computed"
                                            ? "border-purple-300 text-purple-800 dark:border-purple-800 dark:text-purple-300"
                                            : "border-emerald-300 text-emerald-800 dark:border-emerald-800 dark:text-emerald-300"
                                        )}
                                      >
                                        {row.kind}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">None</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="text-xs font-gc-med text-muted-foreground">
                          User message template
                        </div>
                        <div className="text-sm text-foreground font-gc-reg font-mono bg-background border border-border p-3 rounded-lg whitespace-pre-wrap">
                          {(meta.generation_user_template as string) || "—"}
                        </div>
                      </div>
                      {meta.generation_user_example ? (
                        <div className="space-y-2">
                          <div className="text-xs font-gc-med text-muted-foreground">
                            Example user message
                          </div>
                          <div className="text-sm text-foreground font-gc-reg bg-background border border-border p-3 rounded-lg whitespace-pre-wrap">
                            {meta.generation_user_example as string}
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </AiReviewSection>

                <AiReviewSection title="Responses">
                  <SummaryRow label="Teacher type">
                    <Badge variant="outline" className="text-xs w-fit">
                      {teacherTypeLabel(meta.generation_teacher_type)}
                    </Badge>
                  </SummaryRow>
                  <SummaryRow label="Teacher">
                    {meta.generation_model ? (
                      <span className="font-mono text-xs sm:text-sm break-all">
                        {formatModelLabel(String(meta.generation_model))}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </SummaryRow>
                  {showWorkflowInput ? (
                    <div className="space-y-2">
                      <div className="text-xs font-gc-med text-muted-foreground">
                        Workflow input to teacher
                      </div>
                      <pre className="text-xs text-foreground font-mono bg-background border border-border p-3 rounded-lg whitespace-pre-wrap overflow-x-auto max-h-48 overflow-y-auto">
                        {wfInput}
                      </pre>
                    </div>
                  ) : null}
                  {meta.generation_teacher_type === "model" &&
                  String(meta.generation_teacher_system_prompt || "").trim() ? (
                    <div className="space-y-2">
                      <div className="text-xs font-gc-med text-muted-foreground">
                        Teacher system prompt
                      </div>
                      <div className="text-sm text-foreground font-gc-reg font-mono bg-background border border-border p-3 rounded-lg whitespace-pre-wrap">
                        {meta.generation_teacher_system_prompt as string}
                      </div>
                    </div>
                  ) : null}
                  {hasResponseSchemaSection ? (
                    <div className="space-y-3">
                      <div className="text-xs font-gc-med text-muted-foreground">Response schema</div>
                      {schemaFields.length > 0 ? (
                        <div className="rounded-md border border-border overflow-hidden bg-background">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-muted/50">
                                <th className="text-left px-3 py-2 font-gc-med text-muted-foreground">Field</th>
                                <th className="text-left px-3 py-2 font-gc-med text-muted-foreground">Type</th>
                                <th className="text-left px-3 py-2 font-gc-med text-muted-foreground">Description</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {schemaFields.map((field, i) => (
                                <tr key={i}>
                                  <td className="px-3 py-2 font-mono text-foreground">{field.name}</td>
                                  <td className="px-3 py-2 text-muted-foreground">{field.type}</td>
                                  <td className="px-3 py-2 text-muted-foreground">{field.description || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                      {responseValuePath ? (
                        <div className="space-y-1.5">
                          <div className="text-xs font-gc-med text-muted-foreground">Response value path</div>
                          <code className="block text-xs font-mono break-all bg-background border border-border px-2 py-1.5 rounded-md">
                            {responseValuePath}
                          </code>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {meta.generation_teacher_type === "model" && meta.generation_max_tokens ? (
                    <SummaryRow label="Max output tokens">
                      {(meta.generation_max_tokens as number).toLocaleString()}
                    </SummaryRow>
                  ) : null}
                  {meta.generation_response_example ? (
                    <div className="space-y-2">
                      <div className="text-xs font-gc-med text-muted-foreground">Example response</div>
                      <pre className="text-xs text-foreground font-mono bg-background border border-border p-3 rounded-lg whitespace-pre-wrap overflow-x-auto">
                        {meta.generation_response_example as string}
                      </pre>
                    </div>
                  ) : null}
                  <SummaryRow label="Reasoning traces">
                    {meta.generation_include_reasoning ? "Included" : "Not included"}
                  </SummaryRow>
                </AiReviewSection>

                {reviewCostEstimate && genModelRef && genSize > 0 && (
                    <Card className="border-border bg-background">
                      <CardHeader className="pb-4">
                        <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                          <DollarSignIcon className="w-5 h-5 text-maitai-green" />
                          Estimated generation cost (full run)
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="space-y-1">
                            <div className="text-xs text-muted-foreground font-gc-reg">
                              Input tokens (total)
                            </div>
                            <div className="text-lg font-gc-med text-foreground">
                              {formatTokenCount(reviewCostEstimate.inputTokens)}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-xs text-muted-foreground font-gc-reg">
                              Output tokens (total)
                            </div>
                            <div className="text-lg font-gc-med text-foreground">
                              {formatTokenCount(reviewCostEstimate.outputTokens)}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-xs text-muted-foreground font-gc-reg">
                              Estimated total
                            </div>
                            <div className="text-lg font-gc-demi-bold text-maitai-green">
                              ~${reviewCostEstimate.costUsd.toFixed(2)}
                            </div>
                          </div>
                        </div>
                        {reviewCostEstimate.lineItems &&
                          reviewCostEstimate.lineItems.length > 0 && (
                            <div className="rounded-md border border-border overflow-hidden mb-4">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-muted/50">
                                    <th className="text-left px-3 py-2 font-gc-med text-muted-foreground">
                                      Component
                                    </th>
                                    <th className="text-right px-3 py-2 font-gc-med text-muted-foreground">
                                      Calls
                                    </th>
                                    <th className="text-right px-3 py-2 font-gc-med text-muted-foreground">
                                      ~USD
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                  {reviewCostEstimate.lineItems.map((row) => (
                                    <tr key={row.key}>
                                      <td className="px-3 py-2 text-foreground">
                                        <div className="font-gc-med">{row.label}</div>
                                        {row.note ? (
                                          <div className="text-muted-foreground font-gc-reg mt-0.5">
                                            {row.note}
                                          </div>
                                        ) : null}
                                      </td>
                                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                                        {row.calls.toLocaleString()}
                                      </td>
                                      <td className="px-3 py-2 text-right tabular-nums font-gc-med">
                                        ${row.usd.toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-2">
                          <div className="flex items-start gap-2">
                            <InfoIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                            <p className="text-xs text-blue-800 dark:text-blue-200">
                              {reviewCostEstimate.lineItems &&
                              reviewCostEstimate.lineItems.length > 0 ? (
                                <>
                                  Heuristic breakdown (teacher, seed model, supporting workflows). Matches Preview
                                  step.
                                  {reviewCostEstimate.isEstimatedPricing &&
                                    " Some pricing uses defaults."}{" "}
                                  Actual cost may vary.
                                </>
                              ) : (
                                <>
                                  Rough teacher-only fallback
                                  {genModelRef
                                    ? ` (${formatModelLabel(genModelRef)})`
                                    : ""}
                                  .
                                  {reviewCostEstimate.isEstimatedPricing &&
                                    " Pricing approximate."}
                                </>
                              )}
                            </p>
                          </div>
                          {reviewCostEstimate.assumptions &&
                            reviewCostEstimate.assumptions.length > 0 && (
                              <ul className="text-[11px] text-blue-800/90 dark:text-blue-200/90 list-disc pl-4 space-y-0.5">
                                {reviewCostEstimate.assumptions.map((a) => (
                                  <li key={a}>{a}</li>
                                ))}
                              </ul>
                            )}
                        </div>
                      </CardContent>
                    </Card>
                  )}
              </div>
            );
          })()}

          <SummarySection title="Tags" icon={<TagIcon className="w-4 h-4" />}>
            <div className="flex items-center gap-2 flex-wrap">
              {meta.tags && Array.isArray(meta.tags) && meta.tags.length > 0 ? (
                meta.tags.map((tag: string) => {
                  const config = getTagDisplayConfig(tag);
                  return (
                    <Badge
                      key={tag}
                      variant="outline"
                      className={cn("text-xs", config.className)}
                    >
                      {config.label}
                    </Badge>
                  );
                })
              ) : (
                <span className="text-sm text-muted-foreground">No tags selected</span>
              )}
            </div>
          </SummarySection>
        </CardContent>
      </Card>

      {/* Ready State */}
      <Card className="border-maitai-green/20 bg-maitai-green/5">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="w-5 h-5 text-maitai-green mt-0.5" />
            <div className="space-y-1">
              <div className="text-sm font-gc-med text-foreground">Ready to Create</div>
              <div className="text-sm text-muted-foreground">
                Your dataset is ready to be created. Click the 'Create Dataset' button below to proceed.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DatasetReview;
